import { request } from "../api/client";
import type { ParentTokens } from "../stores/parentSession";

/**
 * A3/A4 — the parent's second door, and the one that can actually send a code.
 *
 * 🔴 WHY A SECOND DOOR EXISTS AT ALL, WRITTEN DOWN SO NOBODY DELETES THE FIRST.
 *
 * The WhatsApp OTP path beside this one is complete and correct: a challenge, a
 * five-minute TTL, five attempts, identical generic answers on request and
 * verify. What it cannot do is DELIVER, because `otp_login` is a Meta template
 * that has never been submitted for approval — so on the day a real parent types
 * a real number, nothing arrives. Firebase's phone auth sends an SMS through
 * Google, needs no template approval, and reaches a parent who does not use
 * WhatsApp at all.
 *
 * ⚠️ SO THIS IS AN ALTERNATIVE, NOT A REPLACEMENT. `firebaseConfigured()` is
 * false on a build with no `VITE_FIREBASE_*` values, and every caller falls back
 * to the WhatsApp path unchanged. A deployment chooses; neither path is deleted.
 *
 * 🔴 AND THE SESSION STILL COMES FROM OUR SERVER. Firebase proves ONE fact — the
 * person holds this phone number — and the ID token that says so is exchanged at
 * `POST /auth/parent/firebase` for a Kalqy access token. A Firebase token is
 * never a session here: it says nothing about which children a parent has, and
 * `FirebaseAuthService` refuses a number that belongs to no active parent. A
 * client that treated `signInWithPhoneNumber` as "signed in" would be letting
 * anyone with any phone number in.
 */

/**
 * The three values a browser needs to talk to a Firebase project.
 *
 * ⚠️ THEY ARE PUBLIC, AND THAT IS NOT A LEAK. An `apiKey` here identifies the
 * project; it authorises nothing on its own. What stops a stranger using it is
 * the authorised-domains list in the Firebase console and the reCAPTCHA below —
 * and, past that, our own server refusing a number it does not know. `.env`'s
 * header already says nothing secret may live behind `VITE_`.
 */
interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
}

function readConfig(): FirebaseConfig | null {
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY ?? "";
  const authDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? "";
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID ?? "";
  /* 🔴 ALL THREE OR NONE. Two of three is a build that renders the Firebase
     screen and then fails inside the SDK with a message no parent can act on;
     the honest reading of a half-filled config is "not configured". */
  if (apiKey === "" || authDomain === "" || projectId === "") return null;
  return { apiKey, authDomain, projectId };
}

/** Does this build have a Firebase project to send through? */
export function firebaseConfigured(): boolean {
  return readConfig() !== null;
}

/**
 * The id of the element reCAPTCHA anchors itself to.
 *
 * ⚠️ IT IS CREATED HERE RATHER THAN RENDERED BY A SCREEN. The verifier needs a
 * live DOM node before `signInWithPhoneNumber` is called, and a `<div>` that
 * three screens must remember to include is a `<div>` that will eventually be
 * missing on one of them — which fails at the moment a parent presses the
 * button. Invisible mode draws only a badge.
 */
const RECAPTCHA_ID = "kalqy-recaptcha";

/**
 * What `sendPhoneCode` hands back to `confirmPhoneCode`, kept in module state.
 *
 * 🔴 MODULE STATE, NOT REACT STATE, BECAUSE THE TWO SCREENS ARE SEPARATE ROUTES.
 * A3 sends the code and navigates away; A4 mounts fresh and must confirm against
 * the SAME confirmation object. Threading a non-serialisable SDK handle through
 * a hash-router hop is not possible, and re-creating it would send a second SMS.
 *
 * ⚠️ IT IS DELIBERATELY NOT PERSISTED. A reload loses it, and losing it is the
 * correct outcome: the parent goes back a screen and asks for a new code. A
 * confirmation handle in `localStorage` would be a credential at rest.
 */
let pending: {
  verify: (code: string) => Promise<string>;
  phone: string;
} | null = null;

/** Cleared on the way out — see the note on `pending`. */
export function forgetPendingCode(): void {
  pending = null;
}

/** Is there a code outstanding for this number? */
export function hasPendingCode(phone: string): boolean {
  return pending !== null && pending.phone === phone;
}

/**
 * The SDK, loaded the first time a parent asks for a code.
 *
 * 🔴 A STATIC IMPORT WOULD PUT ~200 KB OF FIREBASE IN THE BUNDLE EVERY CHILD
 * DOWNLOADS. This app runs on a shared school tablet on a school connection, and
 * the phone-auth path is used by one tier, once, on one screen. Vite splits a
 * dynamic import into its own chunk, so nobody who never signs in as a parent
 * ever fetches it.
 */
async function loadAuth(config: FirebaseConfig) {
  const [{ initializeApp, getApps, getApp }, auth] = await Promise.all([
    import("@firebase/app"),
    import("@firebase/auth"),
  ]);
  /* `getApps()` first: HMR in dev re-runs this module, and initialising twice
     throws rather than returning the existing app. */
  const app = getApps().length > 0 ? getApp() : initializeApp(config);
  return { auth, instance: auth.getAuth(app) };
}

/**
 * Ask Google to send an SMS to this number.
 *
 * Resolves once the SMS has been ACCEPTED for delivery — the same contract as
 * `requestParentOtp`, so the router's hop to A4 does not change.
 *
 * ⚠️ IT THROWS WITH A `status` THE SCREENS ALREADY UNDERSTAND. `EntryScreens`
 * branches on 429 and "everything else"; Firebase's own error codes are
 * strings, so they are mapped here rather than teaching two screens a second
 * error vocabulary.
 */
export async function sendPhoneCode(phone: string): Promise<void> {
  const config = readConfig();
  if (config === null)
    throw Object.assign(new Error("Firebase is not configured"), {
      status: 503,
    });

  const { auth, instance } = await loadAuth(config);

  /*
   * 🔴 THE LANGUAGE OF THE SMS FOLLOWS THE DEVICE, NOT THE APP. The OTP text is
   * Google's template, not ours — `useDeviceLanguage` hands it the phone's
   * locale so a parent reads it in whatever their handset is set to. We do not
   * control this template and should not pretend to.
   */
  instance.useDeviceLanguage();

  let holder = document.getElementById(RECAPTCHA_ID);
  if (holder === null) {
    holder = document.createElement("div");
    holder.id = RECAPTCHA_ID;
    document.body.appendChild(holder);
  }

  try {
    /*
     * ⚠️ A FRESH VERIFIER PER REQUEST. reCAPTCHA tokens are single-use: reusing
     * one verifier makes the SECOND "send again" fail with an error a parent
     * reads as "my number is wrong". It is cleared below either way.
     */
    const verifier = new auth.RecaptchaVerifier(instance, RECAPTCHA_ID, {
      size: "invisible",
    });
    try {
      const confirmation = await auth.signInWithPhoneNumber(
        instance,
        phone,
        verifier,
      );
      pending = {
        phone,
        verify: async (code: string) => {
          const credential = await confirmation.confirm(code);
          /* The ID token, not the user object — it is the only part our server
             will look at, and it is signed by Google. */
          return credential.user.getIdToken();
        },
      };
    } finally {
      verifier.clear();
    }
  } catch (err) {
    throw asHttpish(err);
  }
}

/**
 * Confirm the six digits, then trade Google's proof for a Kalqy session.
 *
 * 🔴 TWO STEPS, AND THE SECOND ONE IS THE REAL SIGN-IN. Step one proves the
 * phone; step two asks our server whether that phone belongs to a parent. A
 * stranger's number passes step one and is refused at step two with a 401 — the
 * same generic refusal A4 already draws for a wrong code, which is exactly
 * right: distinguishing them would say which numbers are registered.
 */
export async function confirmPhoneCode(code: string): Promise<ParentTokens> {
  if (pending === null) {
    throw Object.assign(new Error("Ask for a code first"), { status: 401 });
  }

  let idToken: string;
  try {
    idToken = await pending.verify(code);
  } catch (err) {
    throw asHttpish(err);
  }

  const tokens = await request<ParentTokens>("/auth/parent/firebase", {
    method: "POST",
    body: { idToken },
    context: "public",
  });
  /* Spent — a confirmed code must not be confirmable twice. */
  pending = null;
  return tokens;
}

/**
 * A Firebase error → the `{ status }` shape every screen in this app branches on.
 *
 * ⚠️ ONLY TWO DISTINCTIONS ARE MADE, AND THAT IS DELIBERATE. "Too many requests"
 * is worth its own sentence because waiting fixes it. Everything else — a wrong
 * code, an expired one, a malformed number, a blocked domain — becomes the
 * generic refusal, because A4's rule is that a parent gets one sentence for
 * every refusal. Firebase's `auth/invalid-verification-code` and
 * `auth/code-expired` are the two cases behind "wrong, or expired".
 */
/**
 * Codes that mean *this deployment is wrong*, not *this parent is wrong*.
 *
 * 🔴 THEY WERE ALL 401, AND THAT IS WHY A BROKEN PROJECT LOOKED LIKE A BAD CODE.
 * Every `auth/*` failure collapsed to the generic refusal, so a Firebase project
 * with phone sign-in switched off, an origin missing from the authorised domain
 * list, or an API key restricted away from Identity Toolkit all surfaced to the
 * parent as *"couldn't reach Kalqy, check the connection"* — and to whoever was
 * debugging it as nothing at all.
 *
 * ⚠️ NONE OF THESE CAN HAPPEN BECAUSE OF SOMETHING A PARENT TYPED. They are
 * decided before the number is ever sent, which is exactly what makes them safe
 * to report specifically: a misconfigured project is misconfigured identically
 * for a registered number and a stranger's, so saying so leaks nothing.
 */
const DEPLOYMENT_FAULTS: ReadonlySet<string> = new Set([
  /* This origin is not in the project's authorised domains. */
  "auth/unauthorized-domain",
  /* Phone sign-in is not switched on for the project. */
  "auth/operation-not-allowed",
  /* The reCAPTCHA token was rejected — the usual cause of a send that never sends. */
  "auth/invalid-app-credential",
  "auth/missing-app-credential",
  "auth/captcha-check-failed",
  "auth/app-not-authorized",
  /* The key is wrong, or restricted away from the Identity Toolkit API. */
  "auth/invalid-api-key",
  "auth/api-key-not-valid",
  /* Real SMS needs a billed project; test numbers do not, but the send still fails. */
  "auth/billing-not-enabled",
]);

/**
 * A Firebase error → the `{ status }` shape every screen in this app branches on.
 *
 * ⚠️ THREE DISTINCTIONS NOW, NOT TWO. "Too many requests" keeps its own sentence
 * because waiting fixes it. A deployment fault gets 503, because nothing the
 * parent does will ever fix it and telling them to check their connection sends
 * them after the wrong thing. Everything else — a wrong code, an expired one, a
 * malformed number — is still the one generic refusal A4 requires.
 */
function asHttpish(err: unknown): Error & { status: number } {
  const code = (err as { code?: string } | null)?.code ?? "";
  const status =
    code === "auth/too-many-requests" || code === "auth/quota-exceeded"
      ? 429
      : DEPLOYMENT_FAULTS.has(code)
        ? 503
        : code.startsWith("auth/")
          ? 401
          : 0;

  /*
   * 🔴 THE CODE, IN THE CONSOLE, IN DEV. This is the one thing that was missing
   * while parent sign-in was failing: the SDK's reason was mapped to a status,
   * the status to a sentence, and the reason itself was never written down
   * anywhere a developer could read it.
   *
   * ⚠️ DEV ONLY, AND A CODE RATHER THAN THE ERROR. `auth/unauthorized-domain` is
   * a fact about the project; the SDK's error object can carry the phone number
   * in its message, and this is a browser console on a shared machine.
   */
  if (import.meta.env.DEV && code !== "") {
    console.error(`[firebase-phone] ${code} (mapped to ${String(status)})`);
  }

  return Object.assign(new Error(code === "" ? "Sign-in failed" : code), {
    status,
  });
}
